import { FC, useState, useEffect } from "react";
import Image, { IImageProps } from "apps/website/components/base/Image/Image";
import { Theme, themeRootClassMap } from "apps/website/maps/Theme.map";

import Tag from "../../base/Tag/Tag";
import TextControl from "../../base/Text/TextControl/TextControl";

import { LAYOUT_CLASSES } from "./shared/constants";

export interface IImageCompareFrameProps {
  beforeImage: IImageProps;
  afterImage: IImageProps;
  layout?: "stacked" | "side-by-side";
  showTags?: boolean;
  frameSrc?: string;
  theme?: Theme;
  animate?: boolean;
  animationInterval?: number;
}

const ImageCompareFrame: FC<IImageCompareFrameProps> = ({
  beforeImage,
  afterImage,
  layout = "stacked",
  showTags = true,
  frameSrc = "/images/features/team/frame.png",
  theme = "default",
  animate = false,
  animationInterval = 1000,
}) => {
  const [ currentLayout, setCurrentLayout ] = useState(layout);

  useEffect(() => {
    if (!animate) {
      setCurrentLayout(layout);
      return;
    }

    const intervalId = setInterval(() => {
      setCurrentLayout((prev) => (prev === "stacked" ? "side-by-side" : "stacked"));
    }, animationInterval);

    return () => clearInterval(intervalId);
  }, [ animate, layout, animationInterval ]);

  const isStacked = currentLayout === "stacked";

  const beforeImageContainerClass = `relative w-[172px] md:w-[230px] ${isStacked ? "rotate-1" : ""} transition-all duration-500`;
  const afterImageContainerClass = `relative w-[172px] md:w-[230px] ${isStacked ? "absolute top-0 left-0 -rotate-2 -translate-x-4 translate-y-4" : ""} transition-all duration-500`;
  const imageClass = `absolute inset-[4px] overflow-hidden ${themeRootClassMap[theme]}`;
  const frameContainerClass = "relative w-[172px] h-[230px] md:w-[230px] md:h-[307px]";
  const tagClass = "absolute bottom-2 left-1/2 -translate-x-1/2 z-10";

  return (
    <div data-component="ImageCompareFrame" data-theme={theme} className="relative flex items-center justify-center md:w-[460px]">
      { /* Parent container for images */ }
      <div data-testid="image-comparison-container" className={`relative flex ${isStacked ? LAYOUT_CLASSES.STACKED : LAYOUT_CLASSES.SIDE_BY_SIDE} transition-all duration-500`}>

        { /* Before Image */ }
        <div data-testid="before-image-container" className={beforeImageContainerClass}>
          { /* Frame as the container */ }
          <div className={frameContainerClass}>
            <Image
              image={{
                src: frameSrc,
                width: 230,
                height: 307,
              }}
              alt="Frame"
              cover
            />
            { /* Image inside frame */ }
            <div className={imageClass}>
              <Image image={beforeImage} alt="Before Image" cover />
              { showTags && (
                <Tag theme={"dark"} className={tagClass}>
                  <TextControl size={1} className="text-white">Before</TextControl>
                </Tag>
              ) }
            </div>
          </div>
        </div>

        { /* After Image */ }
        <div data-testid="after-image-container" className={afterImageContainerClass}>
          { /* Frame as the container */ }
          <div className={frameContainerClass}>
            <Image
              image={{
                src: frameSrc,
                width: 230,
                height: 307,
              }}
              alt="Frame"
              cover
            />
            { /* Image inside frame */ }
            <div className={imageClass}>
              <Image image={afterImage} alt="After Image" cover />
              { showTags && (
                <Tag theme={"brand"} className={tagClass}>
                  <TextControl size={1}>After</TextControl>
                </Tag>
              ) }
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default ImageCompareFrame;
